/**
 * Future Home Plan Foundation (FHPF) - Client-Side Cryptographic Engine
 * 
 * Runs 100% in the user's browser using the native Web Crypto API (SubtleCrypto).
 * Provides mathematically independent verification of:
 * 1. The SHA-256 tamper-evident ledger hash chain.
 * 2. The multi-party entropy commitment-and-reveal lottery draw.
 */

import type { LedgerEntry } from '@shared/types';

export const GENESIS_PREV_HASH = '0000000000000000000000000000000000000000000000000000000000000000';

/**
 * Deterministically serializes any JavaScript object or primitive into
 * canonical RFC 8785 JSON format (sorted object keys, strict UTF-8, no whitespace).
 */
export function canonicalJsonStringify(obj: unknown): string {
  if (obj === null || typeof obj !== 'object') {
    return JSON.stringify(obj);
  }

  if (Array.isArray(obj)) {
    const serializedElements = obj.map((item) => canonicalJsonStringify(item));
    return `[${serializedElements.join(',')}]`;
  }

  const record = obj as Record<string, unknown>;
  const sortedKeys = Object.keys(record).sort();
  const serializedEntries = sortedKeys
    .filter((k) => record[k] !== undefined)
    .map((k) => `${JSON.stringify(k)}:${canonicalJsonStringify(record[k])}`);

  return `{${serializedEntries.join(',')}}`;
}

/**
 * Computes SHA-256 hash in lowercase hex using the browser's native Web Crypto API.
 */
export async function sha256Browser(content: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Computes HMAC-SHA256 in lowercase hex using Web Crypto API.
 */
export async function hmacSha256Browser(keyStr: string, dataStr: string): Promise<string> {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(keyStr);
  const data = encoder.encode(dataStr);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, data);
  const hashArray = Array.from(new Uint8Array(signature));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Extracts canonical ledger fields for hashing.
 */
export function extractCanonicalLedgerPayload(entry: {
  entryId: string;
  sequenceNumber: number;
  type: string;
  amount: number;
  refId: string;
  note: string;
  createdBy: string;
  createdAt: string;
  prevHash: string;
}): Record<string, unknown> {
  return {
    amount: entry.amount,
    createdAt: entry.createdAt,
    createdBy: entry.createdBy,
    entryId: entry.entryId,
    note: entry.note,
    prevHash: entry.prevHash,
    refId: entry.refId,
    sequenceNumber: entry.sequenceNumber,
    type: entry.type,
  };
}

/**
 * Computes entryHash for a ledger entry.
 */
export async function computeLedgerEntryHashBrowser(entry: {
  entryId: string;
  sequenceNumber: number;
  type: string;
  amount: number;
  refId: string;
  note: string;
  createdBy: string;
  createdAt: string;
  prevHash: string;
}): Promise<string> {
  const canonicalPayload = extractCanonicalLedgerPayload(entry);
  const canonicalJson = canonicalJsonStringify(canonicalPayload);
  return await sha256Browser(canonicalJson);
}

export interface LedgerAuditResult {
  valid: boolean;
  totalEntries: number;
  verifiedCount: number;
  totalCredited: number;
  totalDebited: number;
  netTreasuryBalance: number;
  error?: string;
  brokenAtSequence?: number;
  expectedHash?: string;
  actualHash?: string;
}

/**
 * Full sequential audit of the cryptographic ledger in the browser.
 */
export async function verifyLedgerHashChainBrowser(entries: LedgerEntry[]): Promise<LedgerAuditResult> {
  if (!entries || entries.length === 0) {
    return {
      valid: true,
      totalEntries: 0,
      verifiedCount: 0,
      totalCredited: 0,
      totalDebited: 0,
      netTreasuryBalance: 0,
    };
  }

  const sorted = [...entries].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
  let credited = 0;
  let debited = 0;

  for (let i = 0; i < sorted.length; i++) {
    const entry = sorted[i];
    const expectedSequence = i + 1;

    // Track credit and debit amounts
    if (entry.type === 'contribution' || entry.type === 'late_fee' || entry.type === 'legacy_import') {
      credited += entry.amount;
    } else if (entry.type === 'expense' || entry.type === 'land_payment') {
      debited += entry.amount;
    } else if (entry.type === 'reversal') {
      credited += entry.amount;
    }

    // 1. Verify sequence number
    if (entry.sequenceNumber !== expectedSequence) {
      return {
        valid: false,
        totalEntries: entries.length,
        verifiedCount: i,
        totalCredited: credited,
        totalDebited: debited,
        netTreasuryBalance: credited - debited,
        error: `Sequence discontinuity: expected sequence ${expectedSequence}, but found ${entry.sequenceNumber}`,
        brokenAtSequence: entry.sequenceNumber,
      };
    }

    // 2. Verify previous hash linkage
    if (i === 0) {
      if (entry.prevHash !== GENESIS_PREV_HASH) {
        return {
          valid: false,
          totalEntries: entries.length,
          verifiedCount: 0,
          totalCredited: credited,
          totalDebited: debited,
          netTreasuryBalance: credited - debited,
          error: `Genesis entry prevHash invalid: expected ${GENESIS_PREV_HASH}, found ${entry.prevHash}`,
          brokenAtSequence: 1,
        };
      }
    } else {
      const prevEntry = sorted[i - 1];
      if (entry.prevHash !== prevEntry.entryHash) {
        return {
          valid: false,
          totalEntries: entries.length,
          verifiedCount: i,
          totalCredited: credited,
          totalDebited: debited,
          netTreasuryBalance: credited - debited,
          error: `Hash chain broken at sequence ${entry.sequenceNumber}: prevHash does not match entry ${prevEntry.sequenceNumber}'s hash`,
          brokenAtSequence: entry.sequenceNumber,
          expectedHash: prevEntry.entryHash,
          actualHash: entry.prevHash,
        };
      }
    }

    // 3. Recompute and verify entry hash
    const recomputedHash = await computeLedgerEntryHashBrowser(entry);
    if (recomputedHash !== entry.entryHash) {
      return {
        valid: false,
        totalEntries: entries.length,
        verifiedCount: i,
        totalCredited: credited,
        totalDebited: debited,
        netTreasuryBalance: credited - debited,
        error: `Tampering detected at sequence ${entry.sequenceNumber}: entryHash ${entry.entryHash} does not match computed hash ${recomputedHash}`,
        brokenAtSequence: entry.sequenceNumber,
        expectedHash: recomputedHash,
        actualHash: entry.entryHash,
      };
    }
  }

  return {
    valid: true,
    totalEntries: entries.length,
    verifiedCount: entries.length,
    totalCredited: credited,
    totalDebited: debited,
    netTreasuryBalance: credited - debited,
  };
}

/**
 * Deterministic PRNG using HMAC-SHA256 in browser
 */
class BrowserDeterministicPrng {
  private seed: string;
  private counter: number = 0;

  constructor(seedHex: string) {
    this.seed = seedHex;
  }

  public async nextFloat(): Promise<number> {
    const hex = await hmacSha256Browser(this.seed, this.counter.toString());
    this.counter++;
    const uint32 = parseInt(hex.substring(0, 8), 16);
    return uint32 / 0x100000000;
  }
}

/**
 * Deterministically shuffles an array in browser
 */
export async function deterministicShuffleBrowser<T>(array: T[], seedHex: string): Promise<T[]> {
  const result = [...array];
  const prng = new BrowserDeterministicPrng(seedHex);

  for (let i = result.length - 1; i > 0; i--) {
    const floatVal = await prng.nextFloat();
    const j = Math.floor(floatVal * (i + 1));
    const temp = result[i];
    result[i] = result[j];
    result[j] = temp;
  }

  return result;
}

export interface BrowserLotteryResult {
  lotteryId: string;
  eligibleMemberIds: string[];
  eligibleListHash: string;
  revealedAdminSeed: string;
  revealedMemberSalts: Record<string, string>;
  combinedSeed: string;
  orderedAllocations: Array<{
    rank: number;
    memberId: string;
    flatNumber: string;
  }>;
  verifiedAt: string;
}

/**
 * Browser-side Multi-Party Lottery Verification and Execution
 */
export async function executeAndVerifyLotteryBrowser(params: {
  lotteryId: string;
  eligibleMemberIds: string[];
  flatNumbers: string[];
  adminSeedCommitment: string;
  revealedAdminSeed: string;
  memberCommitments: Record<string, string>;
  revealedMemberSalts: Record<string, string>;
}): Promise<{ valid: boolean; error?: string; result?: BrowserLotteryResult }> {
  const {
    lotteryId,
    eligibleMemberIds,
    flatNumbers,
    adminSeedCommitment,
    revealedAdminSeed,
    memberCommitments,
    revealedMemberSalts,
  } = params;

  // 1. Verify admin seed commitment
  const computedAdminCommitment = await sha256Browser(revealedAdminSeed);
  if (computedAdminCommitment !== adminSeedCommitment) {
    return {
      valid: false,
      error: `Admin seed commitment mismatch! Expected ${adminSeedCommitment}, got ${computedAdminCommitment}`,
    };
  }

  // 2. Verify revealed salts
  for (const memberId of Object.keys(memberCommitments)) {
    const commitment = memberCommitments[memberId];
    const revealed = revealedMemberSalts[memberId];
    if (!revealed) {
      return {
        valid: false,
        error: `Missing revealed salt for member ${memberId}`,
      };
    }
    const computedSaltCommitment = await sha256Browser(revealed);
    if (computedSaltCommitment !== commitment) {
      return {
        valid: false,
        error: `Member ${memberId} salt commitment mismatch!`,
      };
    }
  }

  // 3. Eligible list hash
  const sortedMembers = [...eligibleMemberIds].sort();
  const eligibleListHash = await sha256Browser(canonicalJsonStringify(sortedMembers));

  // 4. Combined seed
  const sortedMemberIds = Object.keys(revealedMemberSalts).sort();
  const concatenatedSalts = sortedMemberIds.map((id) => `${id}:${revealedMemberSalts[id]}`).join('|');
  const fullEntropyPayload = `${revealedAdminSeed}||${concatenatedSalts}`;
  const combinedSeed = await hmacSha256Browser(eligibleListHash, fullEntropyPayload);

  // 5. Shuffle
  const shuffledMembers = await deterministicShuffleBrowser(eligibleMemberIds, combinedSeed);

  // 6. Allocations
  const orderedAllocations = shuffledMembers.map((memberId, index) => ({
    rank: index + 1,
    memberId,
    flatNumber: flatNumbers[index] || `Flat-${index + 1}`,
  }));

  return {
    valid: true,
    result: {
      lotteryId,
      eligibleMemberIds,
      eligibleListHash,
      revealedAdminSeed,
      revealedMemberSalts,
      combinedSeed,
      orderedAllocations,
      verifiedAt: new Date().toISOString(),
    },
  };
}
